<template>
  <div class="admin-page dashboard" v-loading="loading">
    <!-- ============ 页头：标题 + 范围 + 时间窗 ============ -->
    <div class="page-head">
      <div class="head-text">
        <h1>数据总览</h1>
        <p>{{ scopeLabel }} · 近 {{ query.days }} 天</p>
      </div>
      <div class="head-actions">
        <el-select
          v-model="query.schoolId"
          size="small"
          class="scope-select"
          @change="fetchData"
        >
          <el-option
            v-for="item in schoolSelectOptions"
            :key="String(item.id)"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <div class="status-tabs">
          <button
            v-for="d in [7, 30, 90]"
            :key="d"
            type="button"
            class="tab"
            :class="{ 'is-active': query.days === d }"
            @click="switchDays(d)"
          >
            近 {{ d }} 天
          </button>
        </div>
      </div>
    </div>

    <!-- ============ 视觉重心：深色主指标区 ============ -->
    <div class="hero">
      <div class="hero__item" v-for="(item, index) in primaryMetrics" :key="item.label">
        <span class="hero__index">{{ String(index + 1).padStart(2, '0') }}</span>
        <div class="hero__label">{{ item.label }}</div>
        <div class="hero__value">
          {{ item.value }}<span class="hero__unit" v-if="item.unit">{{ item.unit }}</span>
        </div>
        <div class="hero__sub" :class="item.accent ? 'is-accent' : ''">{{ item.sub }}</div>
      </div>
    </div>

    <!-- ============ 次级指标 ============ -->
    <div class="metrics">
      <div class="metric" v-for="item in secondaryMetrics" :key="item.label">
        <div class="metric__bar"></div>
        <div class="metric__label">{{ item.label }}</div>
        <div class="metric__value">{{ item.value }}<span class="metric__unit" v-if="item.unit">{{ item.unit }}</span></div>
        <div class="metric__sub">{{ item.sub }}</div>
      </div>
    </div>

    <!-- ============ 趋势 ============ -->
    <div class="card">
      <div class="card__head">
        <div class="card__title">趋势明细</div>
        <div class="status-tabs">
          <button
            v-for="m in trendOptions"
            :key="m.value"
            type="button"
            class="tab"
            :class="{ 'is-active': trendMetric === m.value }"
            @click="switchTrend(m.value)"
          >
            {{ m.label }}
          </button>
        </div>
      </div>
      <div ref="trendChart" class="chart"></div>
    </div>

    <!-- ============ 排行 ============ -->
    <div class="row">
      <div class="card">
        <div class="card__head">
          <div class="card__title">学校排行 · 用户数</div>
          <span class="card__hint">共 {{ schoolStats.length }} 所学校</span>
        </div>
        <div v-if="!schoolStats.length" class="empty-state">
          <i class="el-icon-office-building" />
          <p>暂无可统计的学校数据</p>
        </div>
        <div v-else class="rank">
          <div class="rank__row" v-for="(item, index) in topByUser" :key="item.schoolId">
            <div class="rank__no" :class="{ 'is-top': index < 3 }">{{ index + 1 }}</div>
            <div class="rank__body">
              <div class="rank__top">
                <span class="rank__name">{{ item.schoolName }}</span>
                <span class="rank__value">{{ item.userCount }} 人</span>
              </div>
              <div class="rank__track">
                <div class="rank__bar" :style="{ width: barWidth(item.userCount, maxUserCount) }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card__head">
          <div class="card__title">学校排行 · 收入</div>
          <span class="card__hint">单位：爱豆</span>
        </div>
        <div v-if="!topByIncome.length" class="empty-state">
          <i class="el-icon-coin" />
          <p>暂无收入数据</p>
        </div>
        <div v-else class="rank">
          <div class="rank__row" v-for="(item, index) in topByIncome" :key="item.schoolId">
            <div class="rank__no" :class="{ 'is-top': index < 3 }">{{ index + 1 }}</div>
            <div class="rank__body">
              <div class="rank__top">
                <span class="rank__name">{{ item.schoolName }}</span>
                <span class="rank__value">{{ toBean(item.income) }} 爱豆</span>
              </div>
              <div class="rank__track">
                <div class="rank__bar rank__bar--warm" :style="{ width: barWidth(item.income, maxIncome) }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ 最近动态 ============ -->
    <div class="row">
      <div class="card">
        <div class="card__head">
          <div class="card__title">最新注册</div>
        </div>
        <div v-if="!latestUsers.length" class="empty-state">
          <i class="el-icon-user" />
          <p>暂无注册用户</p>
        </div>
        <div v-else class="feed">
          <div class="feed__item" v-for="user in latestUsers" :key="user.id">
            <div class="feed__avatar">{{ (user.nickname || '?').charAt(0) }}</div>
            <div class="feed__main">
              <div class="feed__title">{{ user.nickname || '未设置昵称' }}</div>
              <div class="feed__sub">{{ user.schoolName || '未选择学校' }}</div>
            </div>
            <div class="feed__time">{{ shortTime(user.createdAt) }}</div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card__head">
          <div class="card__title">最新动态</div>
        </div>
        <div v-if="!latestMoments.length" class="empty-state">
          <i class="el-icon-chat-line-square" />
          <p>暂无用户动态</p>
        </div>
        <div v-else class="feed">
          <div class="feed__item" v-for="moment in latestMoments" :key="moment.id">
            <div class="feed__avatar feed__avatar--plain">
              <i class="el-icon-chat-line-square"></i>
            </div>
            <div class="feed__main">
              <div class="feed__title feed__title--ellipsis">{{ moment.content }}</div>
              <div class="feed__sub">{{ moment.authorNickname || '匿名' }}</div>
            </div>
            <div class="feed__time">{{ shortTime(moment.createdAt) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { mapGetters } from 'vuex'
import { getDashboard } from '@/api/dashboard'
import { getPublicConfigs } from '@/api/system'
import { toBean, compactNumber, setBeanRate } from '@/utils/bean'

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      query: {
        schoolId: '',
        days: 30
      },
      scopeLabel: '全平台',
      statistics: {},
      charts: {},
      schoolOptions: [],
      latestUsers: [],
      latestMoments: [],
      trendMetric: 'user',
      trendOptions: [
        { value: 'user', label: '注册' },
        { value: 'active', label: '活跃' },
        { value: 'income', label: '收入' },
        { value: 'bean', label: '爱豆消耗' }
      ],
      chartInstance: null
    }
  },
  computed: {
    ...mapGetters(['isSuperAdmin']),
    schoolSelectOptions() {
      const all = {
        id: '',
        name: this.isSuperAdmin ? '全平台（所有学校）' : '我管辖的全部学校'
      }
      return [all].concat(this.schoolOptions)
    },
    /** 第一排：这一屏的重心，只放四个"看整体"的数 */
    primaryMetrics() {
      const s = this.statistics
      const userCount = s.userCount || 0
      const active = s.activeUserCount || 0
      return [
        {
          label: '总用户数',
          value: compactNumber(userCount),
          sub: `今日新增 ${s.todayUserCount || 0} · 近 7 日 ${s.weekUserCount || 0}`
        },
        {
          label: '今日活跃',
          value: compactNumber(active),
          sub: `近 7 日 ${compactNumber(s.weekActiveUserCount || 0)} · 活跃率 ${this.percent(active, userCount)}`
        },
        {
          label: '累计收入',
          value: this.money(s.totalIncome),
          unit: '元',
          sub: `今日 ¥${this.money(s.todayIncome)} · 本月 ¥${this.money(s.monthIncome)}`,
          accent: true
        },
        {
          label: '爱豆流通',
          value: compactNumber(toBean(s.beanBalance)),
          unit: '爱豆',
          sub: `累计发放 ${compactNumber(toBean(s.beanIssued))} · 消耗 ${compactNumber(toBean(s.beanConsumed))}`
        }
      ]
    },
    secondaryMetrics() {
      const s = this.statistics
      return [
        {
          label: '纸条总数',
          value: compactNumber(s.noteCount || 0),
          sub: `今日 +${s.todayNoteCount || 0} · 被取出 ${s.takeCount || 0} 次`
        },
        {
          label: '动态总数',
          value: compactNumber(s.momentCount || 0),
          sub: `今日 +${s.todayMomentCount || 0}`
        },
        {
          label: '认证学生',
          value: compactNumber(s.verifiedUserCount || 0),
          sub: `占用户 ${this.percent(s.verifiedUserCount || 0, s.userCount || 0)}`
        },
        {
          label: '覆盖学校',
          value: compactNumber(s.schoolCount || 0),
          sub: this.isSuperAdmin ? '已开通的学校' : '授权给我的学校'
        }
      ]
    },
    trendData() {
      return (this.charts && this.charts.trendData) || []
    },
    schoolStats() {
      return (this.charts && this.charts.schoolStats) || []
    },
    topByUser() {
      return this.schoolStats.slice(0, 8)
    },
    topByIncome() {
      return ((this.charts && this.charts.incomeTopSchools) || []).slice(0, 8)
    },
    maxUserCount() {
      return Math.max(1, ...this.schoolStats.map(item => item.userCount || 0))
    },
    maxIncome() {
      const values = ((this.charts && this.charts.incomeTopSchools) || []).map(item => Number(item.income) || 0)
      return Math.max(1, ...values)
    }
  },
  created() {
    this.loadBeanRate()
    this.fetchData()
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    loadBeanRate() {
      getPublicConfigs().then(res => {
        const list = (res && res.data) || []
        const rate = list.find(item => item.configKey === 'bean.per_yuan')
        if (rate) {
          setBeanRate(rate.configValue)
        }
      }).catch(() => {})
    },
    fetchData() {
      this.loading = true
      const params = { days: this.query.days }
      if (this.query.schoolId !== '' && this.query.schoolId !== null) {
        params.schoolId = this.query.schoolId
      }
      getDashboard(params).then(res => {
        const data = res.data || {}
        this.statistics = data.statistics || {}
        this.charts = data.charts || {}
        this.schoolOptions = data.schoolOptions || []
        this.scopeLabel = data.scopeLabel || '全平台'
        this.latestUsers = data.latestUsers || []
        this.latestMoments = data.latestMoments || []
        this.loading = false
        this.$nextTick(() => this.renderTrend())
      }).catch(() => {
        this.loading = false
      })
    },
    resizeChart() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },
    switchDays(d) {
      if (this.query.days === d) return
      this.query.days = d
      this.fetchData()
    },
    switchTrend(value) {
      if (this.trendMetric === value) return
      this.trendMetric = value
      this.renderTrend()
    },
    toBean(yuan) {
      return toBean(yuan)
    },
    renderTrend() {
      const dom = this.$refs.trendChart
      if (!dom) {
        return
      }
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(dom)
      }

      const metric = this.trendMetric
      const labels = this.trendData.map(item => item.label)
      let seriesName = ''
      let values = []
      let unit = ''

      if (metric === 'user') {
        seriesName = '新增注册'
        values = this.trendData.map(item => item.userCount || 0)
        unit = '人'
      } else if (metric === 'active') {
        seriesName = '活跃用户'
        values = this.trendData.map(item => item.activeCount || 0)
        unit = '人'
      } else if (metric === 'income') {
        seriesName = '收入'
        values = this.trendData.map(item => Number(item.income) || 0)
        unit = '元'
      } else {
        seriesName = '爱豆消耗'
        values = this.trendData.map(item => toBean(item.beanConsumed))
        unit = '爱豆'
      }

      this.chartInstance.setOption({
        grid: { left: 12, right: 24, top: 30, bottom: 8, containLabel: true },
        tooltip: {
          trigger: 'axis',
          valueFormatter: value => value + ' ' + unit
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: labels,
          axisLine: { lineStyle: { color: '#E8E0E6' } },
          axisLabel: { color: '#8C8191', fontSize: 11 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#F2EEF1' } },
          axisLabel: { color: '#8C8191', fontSize: 11 }
        },
        series: [{
          name: seriesName,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: labels.length <= 32,
          data: values,
          itemStyle: { color: '#E14D68' },
          lineStyle: { width: 2, color: '#E14D68' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(225, 77, 104, 0.22)' },
              { offset: 1, color: 'rgba(225, 77, 104, 0.01)' }
            ])
          }
        }]
      }, true)
    },
    barWidth(value, max) {
      const ratio = (Number(value) || 0) / (max || 1)
      return Math.max(ratio * 100, ratio > 0 ? 4 : 0) + '%'
    },
    money(value) {
      const num = Number(value)
      if (!isFinite(num)) {
        return '0.00'
      }
      return num.toFixed(2)
    },
    percent(part, total) {
      const t = Number(total)
      if (!t) {
        return '0%'
      }
      return ((Number(part) / t) * 100).toFixed(1) + '%'
    },
    shortTime(value) {
      if (!value) {
        return ''
      }
      const text = String(value)
      // 后端返回 "yyyy-MM-dd HH:mm:ss"，这里只留 "MM-dd HH:mm"
      return text.length >= 16 ? text.slice(5, 16) : text
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard {
  padding-bottom: 8px;
}

.scope-select {
  width: 220px;
}

/* ---------------- 深色主指标区（本屏唯一深色块，视觉重心） ---------------- */
.hero {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background:
    radial-gradient(600px 260px at 88% -30%, rgba(225, 77, 104, 0.28) 0%, transparent 62%),
    linear-gradient(135deg, #241c28 0%, #171219 100%);
  border-radius: var(--r-lg);
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 10px 30px -12px rgba(23, 18, 25, 0.42);

  &__item {
    position: relative;
    padding: 24px 24px 22px;
    border-right: 1px solid rgba(255, 255, 255, 0.07);

    &:last-child {
      border-right: none;
    }
  }

  &__label {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    letter-spacing: 1px;
    margin-bottom: 10px;
  }

  &__value {
    font-size: 34px;
    font-weight: 600;
    color: #fff;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }

  &__unit {
    font-size: 14px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.5);
    margin-left: 6px;
  }

  &__sub {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.42);
    margin-top: 10px;
    line-height: 1.5;

    &.is-accent {
      color: #ffb0be;
    }
  }

  &__index {
    position: absolute;
    top: 20px;
    right: 22px;
    font-size: 12px;
    letter-spacing: 1px;
    color: rgba(255, 255, 255, 0.16);
  }
}

/* ---------------- 次级指标 ---------------- */
.metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.metric {
  position: relative;
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-lg);
  padding: 18px 20px 18px 22px;
  overflow: hidden;

  &__bar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--rose-400);
  }

  &__label {
    font-size: 13px;
    color: var(--ink-500);
    margin-bottom: 8px;
  }

  &__value {
    font-size: 24px;
    font-weight: 600;
    color: var(--ink-900);
    font-variant-numeric: tabular-nums;
  }

  &__unit {
    font-size: 12px;
    font-weight: 400;
    color: var(--ink-400);
    margin-left: 4px;
  }

  &__sub {
    font-size: 12px;
    color: var(--ink-400);
    margin-top: 8px;
  }

  &:nth-child(2) .metric__bar {
    background: var(--coral);
  }

  &:nth-child(3) .metric__bar {
    background: var(--gold);
  }

  &:nth-child(4) .metric__bar {
    background: var(--ink-300);
  }
}

/* ---------------- 卡片 ---------------- */
.card {
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-lg);
  padding: 18px 20px 20px;
  margin-bottom: 16px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink-900);
  }

  &__hint {
    font-size: 12px;
    color: var(--ink-400);
  }
}

.chart {
  width: 100%;
  height: 320px;
}

.row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  /* 行内卡片自带下边距会撑破栅格对齐，去掉 */
  > .card {
    margin-bottom: 16px;
  }
}

/* ---------------- 排行 ---------------- */
.rank {
  &__row {
    display: flex;
    align-items: center;
    margin-bottom: 14px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__no {
    width: 22px;
    height: 22px;
    border-radius: var(--r-sm);
    background: var(--ink-100);
    color: var(--ink-400);
    font-size: 12px;
    text-align: center;
    line-height: 22px;
    margin-right: 12px;
    flex-shrink: 0;

    &.is-top {
      background: var(--ink-900);
      color: #fff;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 5px;
  }

  &__name {
    font-size: 13px;
    color: var(--ink-900);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 10px;
  }

  &__value {
    font-size: 13px;
    color: var(--ink-500);
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }

  &__track {
    height: 6px;
    background: var(--ink-100);
    border-radius: 3px;
    overflow: hidden;
  }

  &__bar {
    height: 100%;
    background: linear-gradient(90deg, var(--rose-600), var(--rose-400));
    border-radius: 3px;
    transition: width 0.5s ease;

    &--warm {
      background: linear-gradient(90deg, #d97a3d, var(--coral));
    }
  }
}

/* ---------------- 最近 ---------------- */
.feed {
  &__item {
    display: flex;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid var(--ink-100);

    &:last-child {
      border-bottom: none;
    }
  }

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--rose-50);
    color: var(--rose-600);
    font-size: 14px;
    text-align: center;
    line-height: 32px;
    margin-right: 12px;
    flex-shrink: 0;

    &--plain {
      background: var(--ink-100);
      color: var(--ink-400);
    }
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    color: var(--ink-900);
    margin-bottom: 3px;

    &--ellipsis {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__sub {
    font-size: 12px;
    color: var(--ink-400);
  }

  &__time {
    font-size: 12px;
    color: var(--ink-350);
    flex-shrink: 0;
    margin-left: 12px;
  }
}

/* 图表卡里的分段筛选器尺寸收一档 */
.card__head .status-tabs .tab {
  padding: 5px 13px;
  font-size: 12.5px;
}

@media (max-width: 1280px) {
  .hero,
  .metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  .row {
    grid-template-columns: 1fr;
  }
}
</style>
